import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';




const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonationContext, setImpersonationContext] = useState(null);

  // Helper function to convert camelCase to snake_case
  const toSnakeCase = (obj) => {
    const snakeCaseObj = {};
    for (const key in obj) {
      const snakeKey = key?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
      snakeCaseObj[snakeKey] = obj?.[key];
    }
    return snakeCaseObj;
  };

  // Helper function to convert snake_case to camelCase
  const toCamelCase = (obj) => {
    if (!obj) return null;
    const camelCaseObj = {};
    for (const key in obj) {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      camelCaseObj[camelKey] = obj?.[key];
    }
    return camelCaseObj;
  };

  // Check if profile is complete (for onboarding)
  const isProfileComplete = (profileData) => {
    if (!profileData) return false;
    
    // Check required fields for onboarding completion
    const requiredFields = [
      'phoneNumber',
      'country',
      'businessName',
      'domain',
      'employeeCount',
      'currency'
    ];
    
    return requiredFields?.every(field => {
      const value = profileData?.[field];
      return value !== null && value !== undefined && value !== '';
    });
  };

  // Check for active impersonation
  const checkImpersonation = () => {
    try {
      const impersonationData = localStorage.getItem('admin_impersonation');
      if (impersonationData) {
        const parsedData = JSON.parse(impersonationData);
        setImpersonationContext(parsedData);
        return parsedData;
      }
      setImpersonationContext(null);
      return null;
    } catch (error) {
      console.error('Error checking impersonation:', error);
      setImpersonationContext(null);
      return null;
    }
  };

  // Load user profile from database
  const loadProfile = async (userId, retries = 3) => {
    try {
      // Check if there's an active impersonation
      const impersonation = checkImpersonation();
      
      // Use impersonated user ID if active, otherwise use current user ID
      const profileUserId = impersonation?.impersonatedUserId || userId;
      
      // Retry logic to handle race conditions with trigger
      for (let i = 0; i < retries; i++) {
        const { data, error } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', profileUserId)
          ?.single();

        if (error) {
          // If profile not found and we have retries left, wait and try again
          if (error?.code === 'PGRST116' && i < retries - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
            continue;
          }
          throw error;
        }
        
        // Convert snake_case from database to camelCase for frontend
        const camelCaseProfile = toCamelCase(data);
        setProfile(camelCaseProfile);
        
        // Return the profile for chaining
        return camelCaseProfile;
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      return null;
    }
  };

  useEffect(() => {
    // Check current session
    const initAuth = async () => {
      try {
        // Check for impersonation first
        checkImpersonation();
        
        const { data: { session } } = await supabase?.auth?.getSession();
        setUser(session?.user ?? null);
        
        if (session?.user) {
          await loadProfile(session?.user?.id);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes (CRITICAL: Keep synchronous)
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      
      if (session?.user) {
        loadProfile(session?.user?.id);
      } else {
        setProfile(null);
        setImpersonationContext(null);
      }
    });

    // Listen for storage changes (impersonation updates)
    const handleStorageChange = async (e) => {
      // Handle both direct storage events and custom events
      const isImpersonationChange = e?.key === 'admin_impersonation' || e?.type === 'storage';
      
      if (isImpersonationChange) {
        checkImpersonation();
        // Reload profile with new impersonation context
        if (user) {
          await loadProfile(user?.id);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      subscription?.unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const value = {
    user,
    profile,
    loading,
    impersonationContext,
    isProfileComplete: isProfileComplete(profile),
    getEffectiveUserId: () => {
      const impersonation = checkImpersonation();
      return impersonation?.impersonatedUserId || user?.id;
    },
    refreshProfile: async () => {
      if (user) {
        return await loadProfile(user?.id);
      }
      return null;
    },
    signIn: async (email, password) => {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      return data;
    },
    signUp: async (email, password, fullName, role = 'user') => {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: role,
            avatar_url: '',
          },
        },
      });
      if (error) throw error;
      return data;
    },
    signOut: async () => {
      // CRITICAL FIX: Check for impersonation before signing out
      const impersonation = checkImpersonation();
      
      // If impersonating, clear impersonation state instead of signing out
      if (impersonation) {
        try {
          // Clear impersonation from localStorage
          localStorage.removeItem('admin_impersonation');
          
          // Clear impersonation context state
          setImpersonationContext(null);
          
          // Reload admin profile
          if (user) {
            await loadProfile(user?.id);
          }
          
          // Trigger storage event for other tabs/components
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'admin_impersonation',
            oldValue: JSON.stringify(impersonation),
            newValue: null,
            url: window.location.href,
            storageArea: localStorage
          }));
          
          return;
        } catch (err) {
          console.error('Error ending impersonation:', err);
          throw err;
        }
      }
      
      // Normal sign out if not impersonating
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
    },
    resetPassword: async (email) => {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`,
      });
      if (error) throw error;
    },
    updatePassword: async (newPassword) => {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword,
      });
      if (error) throw error;
      return data;
    },
    updateProfile: async (updates) => {
      if (!user) throw new Error('No user logged in');
      
      try {
        // CRITICAL FIX: Convert camelCase to snake_case for database
        const snakeCaseUpdates = toSnakeCase(updates);
        
        // Add a retry mechanism for profile updates
        let lastError = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          const { data, error } = await supabase
            ?.from('user_profiles')
            ?.update(snakeCaseUpdates)
            ?.eq('id', user?.id)
            ?.select()
            ?.single();
          
          if (!error) {
            // Convert snake_case response back to camelCase for frontend state
            const camelCaseProfile = toCamelCase(data);
            setProfile(camelCaseProfile);
            return camelCaseProfile;
          }
          
          lastError = error;
          
          // If it's a "not found" error, wait and retry
          if (error?.code === 'PGRST116' && attempt < 2) {
            await new Promise(resolve => setTimeout(resolve, 500));
            continue;
          }
          
          // For other errors, throw immediately
          break;
        }
        
        throw lastError;
      } catch (error) {
        console.error('Profile update error:', error);
        throw error;
      }
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};